import { useQuery } from "@tanstack/react-query";
import { Suspense, lazy, useState } from "react";

import { api } from "../../../lib/api";
import {
  FEATURES, btnSm, card, errorText, fmtInt, fmtUsd, input, num, shiftDay, td, th, todaySp,
  type ByFeature, type FeatureKey,
} from "./common";
import type { DailyCost } from "./CostsChart";

const CostsChart = lazy(() => import("./CostsChart"));

/** Custo por cliente do portal (US$): atendimento da IA, análise do portal,
 *  Insights e Relatórios dos agentes de cada conta. */

interface AccountCost {
  account_id: string;
  display_name: string;
  disabled: boolean;
  agent_ids: string[];
  total_usd: number;
  by_feature: ByFeature;
  leads: number;
  cost_per_lead: number | null;
}

interface Overview {
  period: { from: string; to: string };
  total_usd: number;
  by_feature: ByFeature;
  leads: number;
  cost_per_lead: number | null;
  accounts: AccountCost[];
}

interface Detail {
  period: { from: string; to: string };
  account: { id: string; display_name: string };
  total_usd: number;
  by_feature: ByFeature;
  leads: number;
  cost_per_lead: number | null;
  by_agent: { agent_id: string; agent_name: string; total_usd: number; by_feature: ByFeature; leads: number }[];
  by_model: { provider: string; model: string; cost_usd: number; tokens_in: number; tokens_out: number }[];
  daily: DailyCost[];
  analysis: {
    readings: number;
    failures: number;
    leads_analisados: number;
    leituras_por_lead: number | null;
    custo_por_leitura: number | null;
  };
}

type SortKey = "display_name" | "agents" | FeatureKey | "total_usd" | "leads" | "cost_per_lead";

const PRESETS = [7, 30, 90] as const;

function sortValue(a: AccountCost, key: SortKey): string | number | null {
  if (key === "display_name") return a.display_name.toLowerCase();
  if (key === "agents") return a.agent_ids.length;
  if (key === "total_usd" || key === "leads" || key === "cost_per_lead") return a[key];
  return a.by_feature[key];
}

export function CostsTab() {
  const [days, setDays] = useState<number>(30); // 0 = personalizado
  const [from, setFrom] = useState(shiftDay(todaySp(), -29));
  const [to, setTo] = useState(todaySp());
  const [selected, setSelected] = useState<string | null>(null);

  function preset(n: number) {
    setDays(n);
    setTo(todaySp());
    setFrom(shiftDay(todaySp(), -(n - 1)));
  }

  const qs = new URLSearchParams({ date_from: from, date_to: to }).toString();
  const validRange = !!from && !!to && from <= to;

  return (
    <div style={{ maxWidth: 1200, display: "flex", flexDirection: "column", gap: 18 }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: "var(--ink)", marginBottom: 4 }}>
          Custos por cliente
        </div>
        <div style={{ fontSize: 12.5, color: "var(--ink-muted)", lineHeight: 1.5 }}>
          Gasto de IA dos agentes de cada conta do portal, em dólar: atendimento (todas as
          chamadas de modelo das conversas), análise do portal, Insights e Relatórios. Dias no
          horário de Brasília.
        </div>
      </div>

      {/* Período */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, alignItems: "center" }}>
        {PRESETS.map((n) => (
          <PresetBtn key={n} active={days === n} onClick={() => preset(n)}>{n} dias</PresetBtn>
        ))}
        <PresetBtn active={days === 0} onClick={() => setDays(0)}>Personalizado</PresetBtn>
        {days === 0 && (
          <>
            <input type="date" aria-label="De" value={from} max={to} onChange={(e) => setFrom(e.target.value)} style={{ ...input, padding: "5px 8px", fontSize: 12 }} />
            <input type="date" aria-label="Até" value={to} min={from} onChange={(e) => setTo(e.target.value)} style={{ ...input, padding: "5px 8px", fontSize: 12 }} />
          </>
        )}
      </div>

      {!validRange ? (
        <span style={{ fontSize: 12, color: "var(--danger)" }}>Escolha um período válido.</span>
      ) : selected ? (
        <AccountDetail id={selected} qs={qs} onBack={() => setSelected(null)} />
      ) : (
        <OverviewTable qs={qs} onOpen={setSelected} />
      )}
    </div>
  );
}

function PresetBtn({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button onClick={onClick} style={{ padding: "5px 12px", borderRadius: 99, fontSize: 12, cursor: "pointer", background: active ? "var(--brand-soft)" : "var(--surface-hover)", border: `1px solid ${active ? "var(--brand)" : "var(--line)"}`, color: active ? "var(--brand)" : "var(--ink-secondary)" }}>
      {children}
    </button>
  );
}

function OverviewTable({ qs, onOpen }: { qs: string; onOpen: (id: string) => void }) {
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: "total_usd", desc: true });
  const { data, isLoading, error } = useQuery<Overview>({
    queryKey: ["helena-costs", qs],
    queryFn: () => api.get(`/dev/portal-analysis/costs?${qs}`, 60_000),
  });

  if (error) return <span style={{ fontSize: 12, color: "var(--danger)" }}>{errorText(error, "Não foi possível carregar os custos.")}</span>;
  if (isLoading || !data) return <span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Carregando…</span>;
  if (data.accounts.length === 0) return <span style={{ fontSize: 13, color: "var(--ink-dim)" }}>Nenhuma conta no portal.</span>;

  const rows = [...data.accounts].sort((a, b) => {
    const va = sortValue(a, sort.key);
    const vb = sortValue(b, sort.key);
    if (va === vb) return 0;
    if (va == null) return 1; // sem leads (custo/lead "—") sempre no fim
    if (vb == null) return -1;
    const cmp = va < vb ? -1 : 1;
    return sort.desc ? -cmp : cmp;
  });

  const head = (key: SortKey, text: string, right = true) => {
    const active = sort.key === key;
    return (
      <th key={key} style={{ ...th, textAlign: right ? "right" : "left", padding: 0 }} aria-sort={active ? (sort.desc ? "descending" : "ascending") : "none"}>
        <button
          onClick={() => setSort({ key, desc: active ? !sort.desc : key !== "display_name" })}
          style={{ all: "unset", cursor: "pointer", display: "block", width: "100%", boxSizing: "border-box", padding: "8px 10px", textAlign: right ? "right" : "left", color: active ? "var(--ink)" : "var(--ink-muted)", fontWeight: 600, fontSize: 12 }}
        >
          {text}{active ? (sort.desc ? " ↓" : " ↑") : ""}
        </button>
      </th>
    );
  };

  const foot: React.CSSProperties = { ...td, ...num, fontWeight: 600, borderBottom: "none", borderTop: "1px solid var(--line-strong)" };

  return (
    <div style={{ overflowX: "auto" }}>
      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
        <thead>
          <tr>
            {head("display_name", "Cliente", false)}
            {head("agents", "Agentes")}
            {FEATURES.map((f) => head(f.key, f.label))}
            {head("total_usd", "Total")}
            {head("leads", "Leads")}
            {head("cost_per_lead", "Custo/lead")}
          </tr>
        </thead>
        <tbody>
          {rows.map((a) => (
            <tr
              key={a.account_id}
              onClick={() => onOpen(a.account_id)}
              onKeyDown={(e) => { if (e.key === "Enter") onOpen(a.account_id); }}
              tabIndex={0}
              title="Ver detalhe"
              style={{ cursor: "pointer", color: "var(--ink)" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "var(--surface-hover)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "")}
            >
              <td style={{ ...td, fontWeight: 500 }}>
                {a.display_name}
                {a.disabled && <span style={{ marginLeft: 6, fontSize: 11, color: "var(--danger)" }}>desativada</span>}
              </td>
              <td style={{ ...td, ...num }}>{a.agent_ids.length}</td>
              {FEATURES.map((f) => <td key={f.key} style={{ ...td, ...num }}>{fmtUsd(a.by_feature[f.key])}</td>)}
              <td style={{ ...td, ...num, fontWeight: 600 }}>{fmtUsd(a.total_usd)}</td>
              <td style={{ ...td, ...num }}>{fmtInt(a.leads)}</td>
              <td style={{ ...td, ...num }}>{fmtUsd(a.cost_per_lead)}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr title="Agente presente em mais de uma conta entra uma vez só no total">
            <td style={{ ...foot, textAlign: "left" }}>Total</td>
            <td style={foot} />
            {FEATURES.map((f) => <td key={f.key} style={foot}>{fmtUsd(data.by_feature[f.key])}</td>)}
            <td style={foot}>{fmtUsd(data.total_usd)}</td>
            <td style={foot}>{fmtInt(data.leads)}</td>
            <td style={foot}>{fmtUsd(data.cost_per_lead)}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  );
}

function AccountDetail({ id, qs, onBack }: { id: string; qs: string; onBack: () => void }) {
  const { data, isLoading, error } = useQuery<Detail>({
    queryKey: ["helena-costs-detail", id, qs],
    queryFn: () => api.get(`/dev/portal-analysis/costs/${id}?${qs}`, 60_000),
  });

  const back = <button style={{ ...btnSm, alignSelf: "flex-start" }} onClick={onBack}>← Todos os clientes</button>;
  if (error) return <>{back}<span style={{ fontSize: 12, color: "var(--danger)" }}>{errorText(error, "Não foi possível carregar o detalhe.")}</span></>;
  if (isLoading || !data) return <>{back}<span style={{ fontSize: 12, color: "var(--ink-muted)" }}>Carregando…</span></>;

  const tiles: { label: string; value: string; strong?: boolean }[] = [
    { label: "Total", value: fmtUsd(data.total_usd), strong: true },
    ...FEATURES.map((f) => ({ label: f.label, value: fmtUsd(data.by_feature[f.key]) })),
    { label: "Leads", value: fmtInt(data.leads) },
    { label: "Custo por lead", value: fmtUsd(data.cost_per_lead) },
  ];
  const an = data.analysis;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
      {back}
      <div style={{ fontSize: 15, fontWeight: 600, color: "var(--ink)" }}>{data.account.display_name}</div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 10 }}>
        {tiles.map((t) => (
          <div key={t.label} style={{ ...card, padding: "12px 14px" }}>
            <div style={{ fontSize: 11, color: "var(--ink-muted)", marginBottom: 6 }}>{t.label}</div>
            <div style={{ fontSize: t.strong ? 20 : 16, fontWeight: t.strong ? 700 : 600, color: "var(--ink)", fontVariantNumeric: "tabular-nums" }}>{t.value}</div>
          </div>
        ))}
      </div>

      <Section title="Gasto diário por recurso">
        <Suspense fallback={<div style={{ fontSize: 12, color: "var(--ink-muted)", height: 260 }}>Carregando gráfico…</div>}>
          <CostsChart data={data.daily} />
        </Suspense>
      </Section>

      <Section title="Por agente">
        {data.by_agent.length === 0 ? <Empty text="Conta sem agentes." /> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr>
                <th style={th}>Agente</th>
                {FEATURES.map((f) => <th key={f.key} style={{ ...th, textAlign: "right" }}>{f.label}</th>)}
                <th style={{ ...th, textAlign: "right" }}>Total</th>
                <th style={{ ...th, textAlign: "right" }}>Leads</th>
              </tr>
            </thead>
            <tbody>
              {data.by_agent.map((a) => (
                <tr key={a.agent_id} style={{ color: "var(--ink)" }}>
                  <td style={td}>{a.agent_name}</td>
                  {FEATURES.map((f) => <td key={f.key} style={{ ...td, ...num }}>{fmtUsd(a.by_feature[f.key])}</td>)}
                  <td style={{ ...td, ...num, fontWeight: 600 }}>{fmtUsd(a.total_usd)}</td>
                  <td style={{ ...td, ...num }}>{fmtInt(a.leads)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Section>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 18 }}>
        <Section title="Por modelo">
          {data.by_model.length === 0 ? <Empty text="Sem consumo de modelo no período." /> : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr>
                  <th style={th}>Modelo</th>
                  <th style={{ ...th, textAlign: "right" }}>Tokens entrada</th>
                  <th style={{ ...th, textAlign: "right" }}>Tokens saída</th>
                  <th style={{ ...th, textAlign: "right" }}>Custo</th>
                </tr>
              </thead>
              <tbody>
                {data.by_model.map((m) => (
                  <tr key={`${m.provider}/${m.model}`} style={{ color: "var(--ink)" }}>
                    <td style={td}>
                      {m.model || "—"}
                      {m.provider && <span style={{ marginLeft: 6, fontSize: 11.5, color: "var(--ink-muted)" }}>{m.provider}</span>}
                    </td>
                    <td style={{ ...td, ...num }}>{fmtInt(m.tokens_in)}</td>
                    <td style={{ ...td, ...num }}>{fmtInt(m.tokens_out)}</td>
                    <td style={{ ...td, ...num, fontWeight: 600 }}>{fmtUsd(m.cost_usd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Section>

        <Section title="Análise do portal">
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))", gap: 10 }}>
            <Stat label="Leituras" value={fmtInt(an.readings)} />
            <Stat label="Falhas" value={fmtInt(an.failures)} tone={an.failures ? "var(--warn)" : undefined} />
            <Stat label="Leads analisados" value={fmtInt(an.leads_analisados)} />
            <Stat label="Leituras por lead" value={an.leituras_por_lead == null ? "—" : an.leituras_por_lead.toLocaleString("pt-BR")} />
            <Stat label="Custo por leitura" value={fmtUsd(an.custo_por_leitura)} />
          </div>
        </Section>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ ...card, display: "flex", flexDirection: "column", gap: 10 }}>
      <div style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)" }}>{title}</div>
      {children}
    </div>
  );
}

function Stat({ label, value, tone }: { label: string; value: string; tone?: string }) {
  return (
    <div>
      <div style={{ fontSize: 11, color: "var(--ink-muted)", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 16, fontWeight: 600, color: tone ?? "var(--ink)", fontVariantNumeric: "tabular-nums" }}>{value}</div>
    </div>
  );
}

function Empty({ text }: { text: string }) {
  return <span style={{ fontSize: 12.5, color: "var(--ink-dim)" }}>{text}</span>;
}
